import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

type AppRole = "admin" | "agent" | "client" | "co_buyer" | "mls_admin";

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: AppRole[];
  /** If true, agents must be approved to access */
  requireApproval?: boolean;
}

const roleDefaultRoute: Record<string, string> = {
  agent: "/agent-dashboard",
  client: "/client-dashboard",
  admin: "/admin",
  mls_admin: "/mls-admin",
  co_buyer: "/client-dashboard",
};

export const RoleGuard = ({ children, allowedRoles, requireApproval = false }: RoleGuardProps) => {
  const { role, actualRole, agentStatus, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Real admins always have access, even while previewing the app as another role
  const isAdmin = actualRole === "admin";

  if (!isAdmin && (!role || !allowedRoles.includes(role))) {
    const fallback = role ? roleDefaultRoute[role] || "/" : "/";
    return <Navigate to={fallback} replace />;
  }

  // If agent route requires approval and agent is not approved, redirect to onboarding
  // Admins (actual role) always bypass approval
  if (requireApproval && role === "agent" && actualRole !== "admin" && agentStatus) {
    if (!agentStatus.isApproved || !agentStatus.onboardingComplete) {
      return <Navigate to="/agent-onboarding" replace />;
    }
  }

  return <>{children}</>;
};
